// src/components/ArticleList.jsx
import React, {
  useMemo, useState, useEffect, useCallback, useRef,
} from "react";
import PropTypes from "prop-types";
import ArticleCard from "./ArticleCard";

/* ═══════════════════════════════════════════
   CONFIG & I18N
═══════════════════════════════════════════ */
const STORAGE_LANG_KEY = "site_lang";

const i18n = {
  en: {
    noArticles:  "Nothing here yet.",
    noResults:   "No articles match your filters.",
    loadMore:    "Load more stories",
    showing:     (n, t) => `${n} of ${t} stories`,
    sortBy:      "Sort",
    sortOptions: { newest:"Latest", oldest:"Oldest", popular:"Popular", reading:"Quick reads" },
    filterAll:   "All",
    clearFilter: "Clear filters",
    views:       { grid:"Grid", list:"List", compact:"Compact" },
  },
  fr: {
    noArticles:  "Rien ici pour l'instant.",
    noResults:   "Aucun article ne correspond à vos filtres.",
    loadMore:    "Charger plus",
    showing:     (n, t) => `${n} sur ${t}`,
    sortBy:      "Trier",
    sortOptions: { newest:"Récents", oldest:"Anciens", popular:"Populaires", reading:"Courts" },
    filterAll:   "Tout",
    clearFilter: "Réinitialiser",
    views:       { grid:"Grille", list:"Liste", compact:"Compact" },
  },
  ar: {
    noArticles:  "لا يوجد شيء هنا بعد.",
    noResults:   "لا توجد مقالات تطابق الفلاتر.",
    loadMore:    "تحميل المزيد",
    showing:     (n, t) => `${n} من ${t}`,
    sortBy:      "ترتيب",
    sortOptions: { newest:"الأحدث", oldest:"الأقدم", popular:"الأكثر مشاهدة", reading:"قراءة سريعة" },
    filterAll:   "الكل",
    clearFilter: "مسح",
    views:       { grid:"شبكة", list:"قائمة", compact:"مضغوط" },
  },
  de: {
    noArticles:  "Noch nichts hier.",
    noResults:   "Keine Artikel entsprechen Ihren Filtern.",
    loadMore:    "Mehr laden",
    showing:     (n, t) => `${n} von ${t}`,
    sortBy:      "Sortieren",
    sortOptions: { newest:"Neueste", oldest:"Älteste", popular:"Beliebt", reading:"Kurz" },
    filterAll:   "Alle",
    clearFilter: "Zurücksetzen",
    views:       { grid:"Raster", list:"Liste", compact:"Kompakt" },
  },
};

const detectLang = () => {
  try { const v = localStorage.getItem(STORAGE_LANG_KEY); if (v) return v; } catch {}
  return (navigator.language || "en").split("-")[0];
};
const isRTLLang = (l) => ["ar","he","fa","ur"].includes(l);

const sortFn = (arr, mode) => {
  if (!arr?.length) return [];
  const a = [...arr];
  if (mode === "newest")  return a.sort((x,y) => new Date(y.published_at||y.created_at||0) - new Date(x.published_at||x.created_at||0));
  if (mode === "oldest")  return a.sort((x,y) => new Date(x.published_at||x.created_at||0) - new Date(y.published_at||y.created_at||0));
  if (mode === "popular") return a.sort((x,y) => (y.views||0) - (x.views||0));
  if (mode === "reading") {
    const wc = (x) => String(x.content||x.excerpt||"").trim().split(/\s+/).filter(Boolean).length;
    return a.sort((x,y) => wc(x) - wc(y));
  }
  return a;
};

/* ═══════════════════════════════════════════
   SKELETON CARDS
═══════════════════════════════════════════ */
const SkeletonCard = ({ variant = "grid" }) => {
  if (variant === "list") return (
    <div className="al2-skel al2-skel--list">
      <div className="al2-skel-thumb al2-shimmer" />
      <div className="al2-skel-body">
        <div className="al2-shimmer" style={{width:"28%",height:8,borderRadius:4}} />
        <div className="al2-shimmer" style={{width:"88%",height:17,borderRadius:5,marginTop:8}} />
        <div className="al2-shimmer" style={{width:"70%",height:14,borderRadius:5,marginTop:5}} />
        <div className="al2-shimmer" style={{width:"50%",height:10,borderRadius:4,marginTop:12}} />
      </div>
    </div>
  );
  if (variant === "compact") return (
    <div className="al2-skel al2-skel--compact">
      <div className="al2-skel-compact-thumb al2-shimmer" />
      <div style={{flex:1}}>
        <div className="al2-shimmer" style={{width:"32%",height:8,borderRadius:4}} />
        <div className="al2-shimmer" style={{width:"90%",height:13,borderRadius:4,marginTop:5}} />
        <div className="al2-shimmer" style={{width:"65%",height:12,borderRadius:4,marginTop:4}} />
      </div>
    </div>
  );
  return (
    <div className="al2-skel al2-skel--grid">
      <div className="al2-skel-cover al2-shimmer" />
      <div className="al2-skel-body">
        <div className="al2-shimmer" style={{width:"32%",height:9,borderRadius:4}} />
        <div className="al2-shimmer" style={{width:"92%",height:18,borderRadius:5,marginTop:8}} />
        <div className="al2-shimmer" style={{width:"76%",height:16,borderRadius:5,marginTop:5}} />
        <div className="al2-shimmer" style={{width:"100%",height:12,borderRadius:4,marginTop:12}} />
        <div className="al2-shimmer" style={{width:"80%",height:12,borderRadius:4,marginTop:5}} />
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:20,paddingTop:14,borderTop:"1px solid rgba(0,0,0,.06)"}}>
          <div className="al2-shimmer" style={{width:28,height:28,borderRadius:"50%",flexShrink:0}} />
          <div className="al2-shimmer" style={{width:"36%",height:10,borderRadius:4}} />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   EMPTY STATE
═══════════════════════════════════════════ */
const EmptyState = ({ icon, text, action, actionLabel, isRTL }) => (
  <div className={`al2-empty${isRTL?" al2-rtl":""}`}>
    <div className="al2-empty-ring">
      <i className={`bi ${icon}`} />
    </div>
    <p className="al2-empty-txt">{text}</p>
    {action && (
      <button type="button" className="al2-empty-btn" onClick={action}>
        {actionLabel}
      </button>
    )}
  </div>
);

/* ═══════════════════════════════════════════
   TOOLBAR
═══════════════════════════════════════════ */
const VIEW_META = [
  { key:"grid",    icon:"bi-grid-3x3-gap-fill",              label:"Grid"    },
  { key:"list",    icon:"bi-layout-text-sidebar-reverse",    label:"List"    },
  { key:"compact", icon:"bi-list-ul",                        label:"Compact" },
];

const Toolbar = ({ t, isRTL, variant, setVariant, sort, setSort, activeCat, setActiveCat, allCategories, shown, total }) => {
  const [catMore, setCatMore] = useState(false);
  const MAX_INLINE = 5;

  return (
    <div className={`al2-toolbar${isRTL?" al2-rtl":""}`} role="toolbar" aria-label="Article list controls">

      {/* ── Category pills ── */}
      {allCategories.length > 0 && (
        <div className="al2-catbar" role="group" aria-label="Filter by category">
          <button
            type="button"
            className={`al2-pill${!activeCat?" al2-pill--on":""}`}
            onClick={() => setActiveCat(null)}
          >
            {t.filterAll}
            {!activeCat && <span className="al2-pill-count">{total}</span>}
          </button>

          {(catMore ? allCategories : allCategories.slice(0, MAX_INLINE)).map((c) => {
            const key = c.slug||c.id;
            const on  = activeCat === key;
            return (
              <button
                key={key}
                type="button"
                className={`al2-pill${on?" al2-pill--on":""}`}
                onClick={() => setActiveCat(on ? null : key)}
                aria-pressed={on}
              >
                {c.name}
              </button>
            );
          })}

          {allCategories.length > MAX_INLINE && (
            <button
              type="button"
              className="al2-pill al2-pill--more"
              onClick={() => setCatMore((s) => !s)}
            >
              {catMore
                ? <><i className="bi bi-chevron-left" style={{fontSize:"0.6em"}} /> Less</>
                : <>+{allCategories.length - MAX_INLINE}</>
              }
            </button>
          )}
        </div>
      )}

      {/* ── Controls row ── */}
      <div className="al2-ctrl-row">
        <span className="al2-count-lbl" aria-live="polite">{t.showing(shown, total)}</span>

        <div className="al2-ctrl-right">
          {/* Sort */}
          <label className="al2-sort-wrap" aria-label={t.sortBy}>
            <i className="bi bi-sort-down-alt al2-sort-ico" aria-hidden />
            <select
              className="al2-sort-sel"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label={t.sortBy}
            >
              {Object.entries(t.sortOptions).map(([v,lbl]) => (
                <option key={v} value={v}>{lbl}</option>
              ))}
            </select>
            <i className="bi bi-chevron-down al2-sel-chev" aria-hidden />
          </label>

          {/* View toggle */}
          <div className="al2-vtoggle" role="group" aria-label="View style">
            {VIEW_META.map(({ key, icon }) => (
              <button
                key={key}
                type="button"
                className={`al2-vbtn${variant===key?" al2-vbtn--on":""}`}
                onClick={() => setVariant(key)}
                aria-pressed={variant===key}
                title={t.views?.[key] || key}
              >
                <i className={`bi ${icon}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ToolbarSkeleton = () => (
  <div className="al2-toolbar-skel">
    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      {[72,90,78,84].map((w,i) => (
        <div key={i} className="al2-shimmer" style={{width:w,height:32,borderRadius:100}} />
      ))}
    </div>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,gap:8}}>
      <div className="al2-shimmer" style={{width:100,height:12,borderRadius:4}} />
      <div style={{display:"flex",gap:6}}>
        <div className="al2-shimmer" style={{width:106,height:34,borderRadius:8}} />
        <div style={{display:"flex",gap:3}}>
          {[0,1,2].map((i) => (
            <div key={i} className="al2-shimmer" style={{width:34,height:34,borderRadius:8}} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   ARTICLE LIST
═══════════════════════════════════════════ */
/**
 * Props
 * ─────────────────────────────────────────
 * articles        array   — article objects from API
 * loading         bool    — show skeletons while fetching
 * onBookmark      fn      — (article) => void
 * bookmarkedIds   array|Set — IDs already bookmarked
 * defaultVariant  string  — "grid"|"list"|"compact"
 * defaultSort     string  — "newest"|"oldest"|"popular"|"reading"
 * showControls    bool    — show the toolbar
 * pageSize        number  — items per load-more batch  (0 = all)
 * heroFirst       bool    — first article rendered as hero card
 * featuredIds     array   — article IDs pinned to top
 * emptySlot       node    — custom empty-state content
 * skeletonCount   number  — skeletons shown while loading
 * columns         "auto"|1|2|3|4
 */
const ArticleList = ({
  articles       = [],
  loading        = false,
  onBookmark,
  bookmarkedIds  = [],
  defaultVariant = "grid",
  defaultSort    = "newest",
  showControls   = true,
  pageSize       = 9,
  heroFirst      = false,
  featuredIds    = [],
  emptySlot,
  skeletonCount  = 6,
  columns        = "auto",
}) => {
  const [lang,      setLang]      = useState(detectLang);
  const [variant,   setVariant]   = useState(defaultVariant);
  const [sort,      setSort]      = useState(defaultSort);
  const [activeCat, setActiveCat] = useState(null);
  const [visible,   setVisible]   = useState(pageSize || 9999);
  const [waveKey,   setWaveKey]   = useState(0);

  const isRTL = isRTLLang(lang);
  const t     = i18n[lang] || i18n.en;

  /* lang sync */
  useEffect(() => {
    const onStorage = (e) => { if (e.key===STORAGE_LANG_KEY && e.newValue) setLang(e.newValue); };
    const onCustom  = (e) => { const l=e?.detail?.lang; if (l) setLang(l); };
    window.addEventListener("storage",               onStorage);
    window.addEventListener("site:language_changed", onCustom);
    window.addEventListener("site:set_lang",         onCustom);
    return () => {
      window.removeEventListener("storage",               onStorage);
      window.removeEventListener("site:language_changed", onCustom);
      window.removeEventListener("site:set_lang",         onCustom);
    };
  }, []);

  /* reset visible on data/filter change */
  useEffect(() => { setVisible(pageSize||9999); }, [articles, pageSize]);

  /* re-trigger stagger animation when display changes */
  useEffect(() => { setWaveKey((k) => k+1); }, [sort, activeCat, variant]);

  /* collect categories from all articles */
  const allCategories = useMemo(() => {
    const map = new Map();
    (articles||[]).forEach((a) => (a.categories||[]).forEach((c) => {
      if (c?.slug||c?.id) map.set(c.slug||c.id, c);
    }));
    return [...map.values()];
  }, [articles]);

  /* processed (pinned + filtered + sorted) */
  const processed = useMemo(() => {
    let list = Array.isArray(articles) ? [...articles] : [];
    const featSet = new Set((featuredIds||[]).map(String));

    if (featSet.size) {
      const feat = list.filter((a) => featSet.has(String(a.id??a.slug)));
      const rest = list.filter((a) => !featSet.has(String(a.id??a.slug)));
      list = [...feat, ...rest];
    }

    if (activeCat) {
      list = list.filter((a) => (a.categories||[]).some((c) => (c.slug||c.id)===activeCat));
    }

    const pinnedCount = featSet.size
      ? list.filter((a) => featSet.has(String(a.id??a.slug))).length : 0;
    return [
      ...list.slice(0, pinnedCount),
      ...sortFn(list.slice(pinnedCount), sort),
    ];
  }, [articles, sort, activeCat, featuredIds]);

  const page    = useMemo(() => processed.slice(0, visible), [processed, visible]);
  const hasMore = processed.length > visible && (pageSize||0) > 0;
  const bookSet = useMemo(() => new Set((bookmarkedIds||[]).map(String)), [bookmarkedIds]);
  const loadMore    = useCallback(() => setVisible((c) => c+(pageSize||9)), [pageSize]);
  const resetFilter = useCallback(() => { setActiveCat(null); setSort("newest"); }, []);

  /* col class */
  const colCls = useMemo(() => {
    if (variant==="list"||variant==="compact") return "al2-stack";
    if (columns!=="auto") return `al2-cols-${columns}`;
    return "al2-cols-auto";
  }, [variant, columns]);

  const heroArticle  = heroFirst && page.length>0 ? page[0] : null;
  const gridArticles = heroFirst ? page.slice(1) : page;

  /* ── Loading ── */
  if (loading) {
    return (
      <>
        <ArticleListStyles />
        {showControls && <ToolbarSkeleton />}
        <div className={`al2-grid ${colCls}`} aria-busy="true" aria-live="polite">
          {[...Array(skeletonCount)].map((_,i) => (
            <div key={i} className="al2-item-wrap" style={{animationDelay:`${i*55}ms`}}>
              <SkeletonCard variant={variant} />
            </div>
          ))}
        </div>
      </>
    );
  }

  /* ── Empty ── */
  if (!Array.isArray(articles)||articles.length===0) {
    return (
      <>
        <ArticleListStyles />
        <EmptyState icon="bi-newspaper" text={emptySlot||t.noArticles} isRTL={isRTL} />
      </>
    );
  }

  /* ── No results after filter ── */
  if (processed.length===0) {
    return (
      <>
        <ArticleListStyles />
        {showControls && (
          <Toolbar
            t={t} isRTL={isRTL}
            variant={variant} setVariant={setVariant}
            sort={sort} setSort={setSort}
            activeCat={activeCat} setActiveCat={setActiveCat}
            allCategories={allCategories}
            shown={0} total={articles.length}
          />
        )}
        <EmptyState
          icon="bi-funnel"
          text={t.noResults}
          action={resetFilter}
          actionLabel={t.clearFilter}
          isRTL={isRTL}
        />
      </>
    );
  }

  /* ── Main ── */
  return (
    <>
      <ArticleListStyles />

      {showControls && (
        <Toolbar
          t={t} isRTL={isRTL}
          variant={variant} setVariant={setVariant}
          sort={sort} setSort={setSort}
          activeCat={activeCat} setActiveCat={setActiveCat}
          allCategories={allCategories}
          shown={Math.min(visible, processed.length)}
          total={processed.length}
        />
      )}

      {heroArticle && (
        <div className="al2-hero-wrap">
          <ArticleCard
            article={heroArticle}
            variant="hero"
            onBookmark={onBookmark}
            bookmarked={bookSet.has(String(heroArticle.id??heroArticle.slug))}
          />
        </div>
      )}

      {gridArticles.length > 0 && (
        <div
          key={waveKey}
          className={["al2-grid", colCls, "al2-grid--in", isRTL?"al2-rtl":""].filter(Boolean).join(" ")}
          dir={isRTL?"rtl":"ltr"}
        >
          {gridArticles.map((a, idx) => (
            <div
              key={a.id??a.slug??idx}
              className="al2-item-wrap"
              style={{ animationDelay:`${Math.min(idx,10)*55}ms` }}
            >
              <ArticleCard
                article={a}
                variant={variant}
                onBookmark={onBookmark}
                bookmarked={bookSet.has(String(a.id??a.slug))}
              />
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="al2-loadmore-wrap">
          <div className="al2-progress-bar" role="progressbar"
            aria-valuenow={Math.round((Math.min(visible,processed.length)/processed.length)*100)}
            aria-valuemin={0} aria-valuemax={100}>
            <div
              className="al2-progress-fill"
              style={{width:`${Math.round((Math.min(visible,processed.length)/processed.length)*100)}%`}}
            />
          </div>
          <span className="al2-loadmore-count">{t.showing(Math.min(visible,processed.length), processed.length)}</span>
          <button type="button" className="al2-loadmore-btn" onClick={loadMore}>
            <span className="al2-loadmore-txt">{t.loadMore}</span>
            <span className="al2-loadmore-ring" aria-hidden>
              <i className="bi bi-arrow-down" />
            </span>
          </button>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════
   STYLES — injected once into <head>
═══════════════════════════════════════════ */
let _injected = false;
const ArticleListStyles = () => {
  useEffect(() => {
    if (_injected || document.getElementById("al2-css")) return;
    _injected = true;
    const el = document.createElement("style");
    el.id = "al2-css";
    el.textContent = AL2_CSS;
    document.head.appendChild(el);
  }, []);
  return null;
};

const AL2_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

:root {
  --al2-serif:    'DM Serif Display', Georgia, serif;
  --al2-sans:     'DM Sans', 'Helvetica Neue', sans-serif;
  --al2-red:      #dc2f14;
  --al2-red-lt:   rgba(220,47,20,.08);
  --al2-red-dk:   #b52510;
  --al2-ink:      #1a1a1a;
  --al2-ink2:     #5a5a6a;
  --al2-ink3:     #9394a5;
  --al2-bg:       #fafaf8;
  --al2-bg2:      #f2f2ef;
  --al2-surface:  #ffffff;
  --al2-border:   rgba(0,0,0,.07);
  --al2-focus:    rgba(220,47,20,.22);
  --al2-sh:       0 1px 3px rgba(0,0,0,.05),0 1px 2px rgba(0,0,0,.04);
  --al2-sh-md:    0 6px 24px rgba(0,0,0,.09),0 2px 8px rgba(0,0,0,.05);
  --al2-r:        12px;
  --al2-t:        0.22s cubic-bezier(.4,0,.2,1);
}

/* ── scope reset ── */
.al2-grid,.al2-toolbar,.al2-hero-wrap,.al2-loadmore-wrap,.al2-empty,.al2-toolbar-skel {
  font-family: var(--al2-sans);
  box-sizing: border-box;
}
.al2-grid *,.al2-toolbar *,.al2-empty *,.al2-loadmore-wrap * { box-sizing: border-box; }
.al2-rtl { direction: rtl; }

/* ════ GRID ════ */
.al2-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
}
.al2-cols-auto { grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr)); }
.al2-cols-2    { grid-template-columns: repeat(2,1fr); }
.al2-cols-3    { grid-template-columns: repeat(3,1fr); }
.al2-cols-4    { grid-template-columns: repeat(4,1fr); }
.al2-stack     { grid-template-columns: 1fr; gap: 10px; }

@media (max-width:1023px) {
  .al2-cols-4 { grid-template-columns: repeat(2,1fr); }
  .al2-cols-3 { grid-template-columns: repeat(2,1fr); }
}
@media (max-width:639px) {
  .al2-cols-2,.al2-cols-3,.al2-cols-4,.al2-cols-auto { grid-template-columns: 1fr; }
  .al2-grid { gap: 14px; }
  .al2-stack { gap: 8px; }
}

/* stagger reveal */
.al2-item-wrap { opacity:0; transform:translateY(16px); }
.al2-grid--in .al2-item-wrap {
  animation: al2-reveal 0.44s cubic-bezier(.22,1,.36,1) both;
}
@keyframes al2-reveal { to { opacity:1; transform:none; } }

/* hero */
.al2-hero-wrap { margin-bottom: 22px; }

/* ════ TOOLBAR ════ */
.al2-toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
  padding: 13px 15px;
  background: var(--al2-surface);
  border: 1px solid var(--al2-border);
  border-radius: var(--al2-r);
  box-shadow: var(--al2-sh);
}

/* category pill row */
.al2-catbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
@media (max-width:479px) {
  .al2-catbar {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 3px;
  }
  .al2-catbar::-webkit-scrollbar { display: none; }
}

/* pills */
.al2-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 13px;
  border-radius: 100px;
  border: 1.5px solid var(--al2-border);
  background: var(--al2-bg2);
  color: var(--al2-ink2);
  font-size: 0.775rem; font-weight: 600;
  font-family: var(--al2-sans);
  cursor: pointer; white-space: nowrap; flex-shrink: 0; line-height: 1;
  transition: border-color var(--al2-t), color var(--al2-t), background var(--al2-t), box-shadow var(--al2-t);
}
.al2-pill:hover           { border-color:var(--al2-red); color:var(--al2-red); background:var(--al2-red-lt); }
.al2-pill--on             { background:var(--al2-red); color:#fff; border-color:var(--al2-red); box-shadow:0 2px 8px rgba(220,47,20,.28); }
.al2-pill--on:hover       { background:var(--al2-red-dk); border-color:var(--al2-red-dk); color:#fff; }
.al2-pill--more           { border-style:dashed; color:var(--al2-ink3); }
.al2-pill-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; padding: 0 3px;
  border-radius: 100px; background: rgba(255,255,255,.22);
  font-size: 0.6rem; font-weight: 700; line-height: 1;
}

/* controls row */
.al2-ctrl-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
}
.al2-ctrl-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.al2-count-lbl { font-size: 0.73rem; font-weight: 500; color: var(--al2-ink3); white-space: nowrap; }

/* sort */
.al2-sort-wrap {
  display: flex; align-items: center; gap: 0; position: relative;
  background: var(--al2-bg2); border: 1.5px solid var(--al2-border);
  border-radius: 8px; padding: 0 8px 0 10px; cursor: pointer;
  transition: border-color var(--al2-t);
}
.al2-sort-wrap:focus-within { border-color:var(--al2-red); box-shadow:0 0 0 3px var(--al2-focus); }
.al2-sort-ico { font-size: 0.8rem; color: var(--al2-ink3); flex-shrink: 0; }
.al2-sort-sel {
  border: none; background: transparent; outline: none;
  font-size: 0.775rem; font-weight: 600; font-family: var(--al2-sans);
  color: var(--al2-ink);
  padding: 7px 20px 7px 7px;
  appearance: none; -webkit-appearance: none; cursor: pointer;
  min-width: 88px;
}
.al2-sel-chev {
  position: absolute; right: 7px;
  font-size: 0.52rem; color: var(--al2-ink3); pointer-events: none;
}

/* view toggle */
.al2-vtoggle {
  display: flex;
  background: var(--al2-bg2); border: 1.5px solid var(--al2-border);
  border-radius: 9px; padding: 3px; gap: 2px;
}
.al2-vbtn {
  width: 30px; height: 30px; border-radius: 6px;
  border: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8125rem; color: var(--al2-ink3); flex-shrink: 0;
  transition: background var(--al2-t), color var(--al2-t), box-shadow var(--al2-t);
}
.al2-vbtn:hover   { background: rgba(0,0,0,.05); color: var(--al2-ink); }
.al2-vbtn--on     { background: var(--al2-surface); color: var(--al2-red); box-shadow: 0 1px 4px rgba(0,0,0,.1); }

@media (max-width:479px) {
  .al2-sort-ico   { display: none; }
  .al2-sort-sel   { min-width: 72px; font-size: 0.73rem; }
  .al2-vbtn       { width: 28px; height: 28px; font-size: 0.76rem; }
  .al2-count-lbl  { font-size: 0.68rem; }
}

/* toolbar skeleton */
.al2-toolbar-skel {
  margin-bottom: 18px; padding: 13px 15px;
  background: var(--al2-surface); border: 1px solid var(--al2-border);
  border-radius: var(--al2-r);
}

/* ════ EMPTY ════ */
.al2-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: clamp(40px,8vw,80px) 24px; text-align: center;
  font-family: var(--al2-sans);
}
.al2-empty-ring {
  width: 70px; height: 70px; border-radius: 50%;
  border: 2px dashed rgba(0,0,0,.12);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.75rem; color: var(--al2-ink3);
  margin-bottom: 18px;
  animation: al2-float 3.2s ease-in-out infinite;
}
@keyframes al2-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
.al2-empty-txt {
  font-family: var(--al2-serif); font-style: italic;
  font-size: 1.0625rem; color: var(--al2-ink2); margin: 0 0 18px;
}
.al2-empty-btn {
  font-size: 0.82rem; font-weight: 700; font-family: var(--al2-sans);
  padding: 9px 22px; border-radius: 9px;
  background: var(--al2-red); color: #fff; border: none; cursor: pointer;
  transition: background var(--al2-t), transform .12s;
}
.al2-empty-btn:hover  { background: var(--al2-red-dk); }
.al2-empty-btn:active { transform: scale(.96); }

/* ════ LOAD MORE ════ */
.al2-loadmore-wrap {
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; margin-top: 32px;
  font-family: var(--al2-sans);
}
.al2-progress-bar {
  width: min(240px, 72%); height: 3px;
  background: var(--al2-bg2); border-radius: 3px; overflow: hidden;
}
.al2-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--al2-red), #ff6b35);
  border-radius: 3px;
  transition: width 0.55s cubic-bezier(.4,0,.2,1);
}
.al2-loadmore-count { font-size: 0.72rem; color: var(--al2-ink3); font-weight: 500; }
.al2-loadmore-btn {
  display: inline-flex; align-items: center; gap: 12px;
  padding: 11px 28px; border-radius: 100px;
  background: var(--al2-surface); border: 1.5px solid var(--al2-border);
  cursor: pointer; font-family: var(--al2-sans); color: var(--al2-ink);
  box-shadow: var(--al2-sh);
  transition: border-color var(--al2-t), box-shadow var(--al2-t), transform .14s, background var(--al2-t), color var(--al2-t);
}
.al2-loadmore-btn:hover  { border-color:var(--al2-red); box-shadow:var(--al2-sh-md); background:var(--al2-red-lt); color:var(--al2-red); }
.al2-loadmore-btn:active { transform: scale(.97); }
.al2-loadmore-txt  { font-size: 0.875rem; font-weight: 700; }
.al2-loadmore-ring {
  width: 24px; height: 24px; border-radius: 50%;
  border: 1.5px solid currentColor;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.6rem; flex-shrink: 0;
  transition: transform var(--al2-t);
}
.al2-loadmore-btn:hover .al2-loadmore-ring { transform: translateY(2px); }

/* ════ SHIMMER ════ */
@keyframes al2-shimmer {
  0%   { background-position: -500px 0; }
  100% { background-position: 500px 0; }
}
.al2-shimmer {
  background: linear-gradient(90deg, #ebebeb 25%, #f6f6f6 50%, #ebebeb 75%);
  background-size: 1000px 100%;
  animation: al2-shimmer 1.6s ease-in-out infinite;
  border-radius: 6px;
  display: block;
}

.al2-skel--grid {
  background: var(--al2-surface); border: 1px solid var(--al2-border);
  border-radius: var(--al2-r); overflow: hidden;
}
.al2-skel-cover { height: 185px; border-radius: 0; }
.al2-skel-body  { padding: 16px; display: flex; flex-direction: column; gap: 0; }

.al2-skel--list {
  display: flex; align-items: flex-start; gap: 12px; padding: 12px;
  background: var(--al2-surface); border: 1px solid var(--al2-border);
  border-radius: var(--al2-r);
}
.al2-skel-thumb { width: 96px; height: 68px; border-radius: 8px; flex-shrink: 0; }
.al2-skel-body  { flex: 1; min-width: 0; }

.al2-skel--compact {
  display: flex; align-items: center; gap: 10px;
  padding: 8px; border-radius: 8px; background: var(--al2-surface);
}
.al2-skel-compact-thumb { width: 60px; height: 46px; border-radius: 6px; flex-shrink: 0; }

/* ════ FOCUS ════ */
.al2-pill:focus-visible,
.al2-vbtn:focus-visible,
.al2-loadmore-btn:focus-visible,
.al2-empty-btn:focus-visible {
  outline: 2px solid var(--al2-red);
  outline-offset: 2px;
}

/* ════ DARK MODE ════ */
@media (prefers-color-scheme: dark) {
  :root {
    --al2-ink:     #f0f0ee;
    --al2-ink2:    #9394a5;
    --al2-ink3:    #636475;
    --al2-bg:      #0f0f0e;
    --al2-bg2:     #1a1a18;
    --al2-surface: #161614;
    --al2-border:  rgba(255,255,255,.08);
    --al2-sh:      0 1px 3px rgba(0,0,0,.4),0 1px 2px rgba(0,0,0,.3);
    --al2-sh-md:   0 6px 24px rgba(0,0,0,.45),0 2px 8px rgba(0,0,0,.3);
    --al2-red-lt:  rgba(220,47,20,.1);
    --al2-focus:   rgba(220,47,20,.28);
  }
  .al2-shimmer {
    background: linear-gradient(90deg, #232321 25%, #2c2c2a 50%, #232321 75%);
    background-size: 1000px 100%;
  }
  .al2-sort-sel { color: var(--al2-ink); }
  .al2-sort-sel option { background: var(--al2-bg2); }
  .al2-empty-ring { border-color: rgba(255,255,255,.1); }
  .al2-progress-bar { background: #2a2a28; }
  .al2-loadmore-btn:hover { background: rgba(220,47,20,.08); }
  .al2-vbtn--on { background: #252523; }
}

/* ════ PRINT ════ */
@media print {
  .al2-toolbar,.al2-loadmore-wrap { display: none !important; }
  .al2-grid { display: block; }
  .al2-item-wrap { opacity:1 !important; transform:none !important; break-inside:avoid; margin-bottom:24px; }
}
`;

/* ═══════════════════════════════════════════
   PROP TYPES
═══════════════════════════════════════════ */
ArticleList.propTypes = {
  articles:       PropTypes.array,
  loading:        PropTypes.bool,
  onBookmark:     PropTypes.func,
  bookmarkedIds:  PropTypes.oneOfType([PropTypes.array, PropTypes.instanceOf(Set)]),
  defaultVariant: PropTypes.oneOf(["grid","list","compact"]),
  defaultSort:    PropTypes.oneOf(["newest","oldest","popular","reading"]),
  showControls:   PropTypes.bool,
  pageSize:       PropTypes.number,
  heroFirst:      PropTypes.bool,
  featuredIds:    PropTypes.array,
  emptySlot:      PropTypes.node,
  skeletonCount:  PropTypes.number,
  columns:        PropTypes.oneOf(["auto",1,2,3,4]),
};

export default ArticleList;